import api from '@/api';

const CACHE_PREFIX = 'select_values_cache_';
const CHECK_DAYS = 7;

export async function getSelectValuesCached(propertyId, { include_children = false } = {}) {
  const cacheKey = `${CACHE_PREFIX}${propertyId}_${include_children ? 'with_children' : 'no_children'}`;
  const cached = localStorage.getItem(cacheKey);

  const lastCheckedKey = `${cacheKey}_last_checked`;
  const lastChecked = localStorage.getItem(lastCheckedKey);

  const currentDate = new Date();
  const lastCheckedDate = lastChecked ? new Date(lastChecked) : null;
  const daysSinceLastCheck = lastCheckedDate
    ? (currentDate - lastCheckedDate) / (1000 * 60 * 60 * 24)
    : Infinity;

  if (daysSinceLastCheck > CHECK_DAYS) {
    return await checkAndUpdateCache(
      propertyId,
      cached,
      cacheKey,
      lastCheckedKey,
      include_children,
    );
  }

  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      return Array.isArray(parsed?.results) ? parsed.results : [];
    } catch (e) {
      return await loadAllPages(propertyId, cacheKey, lastCheckedKey, include_children);
    }
  }

  return await loadAllPages(propertyId, cacheKey, lastCheckedKey, include_children);
}

async function checkAndUpdateCache(propertyId, cached, cacheKey, lastCheckedKey, include_children) {
  let parsed = null;

  if (cached) {
    try {
      parsed = JSON.parse(cached);
    } catch (e) {
      parsed = null;
    }
  }

  if (!parsed || !Array.isArray(parsed.results)) {
    return await loadAllPages(propertyId, cacheKey, lastCheckedKey, include_children);
  }

  const versionFromCache = parsed.version;
  const cacheLen = parsed.results.length;

  try {
    const response = await api.get(`/attributes/select_values/`, {
      params: { property: propertyId, include_children, page: 1 },
    });

    const versionFromServer = response.data?.version;

    const serverCount = response.data?.count;
    const serverNext = response.data?.next;
    const page1Len = Array.isArray(response.data?.results) ? response.data.results.length : 0;

    if (versionFromServer === versionFromCache) {
      const looksIncomplete = !!serverNext && cacheLen <= page1Len && page1Len > 0;
      const countMismatch = typeof serverCount === 'number' && cacheLen !== serverCount;

      if (looksIncomplete || countMismatch) {
        return await loadAllPages(propertyId, cacheKey, lastCheckedKey, include_children);
      }

      localStorage.setItem(lastCheckedKey, new Date().toISOString());
      return parsed.results;
    }

    return await loadAllPages(propertyId, cacheKey, lastCheckedKey, include_children);
  } catch (e) {
    // если сервер временно недоступен — лучше вернуть кэш
    localStorage.setItem(lastCheckedKey, new Date().toISOString());
    return parsed.results;
  }
}

/**
 * ВАЖНОЕ: грузим все страницы даже если next кривой/нет.
 * Стратегия:
 * 1) page=1 -> получаем results + count + next
 * 2) если next есть -> идём пока next не исчезнет
 * 3) если next нет, но count есть -> идём по page пока fullResults.length < count
 *
 * + защиты:
 * - break если сервер вернул пустые results
 * - break если сервер возвращает те же id снова (защита от "крутит page=1 по кругу")
 */
async function loadAllPages(propertyId, cacheKey, lastCheckedKey, include_children) {
  const fullResults = [];
  let page = 1;
  let version = null;

  let totalCount = null;
  let pageSize = null;

  // защита от дублей/зацикливания
  const seenIds = new Set();

  try {
    while (true) {
      const res = await api.get(`/attributes/select_values/`, {
        params: { property: propertyId, include_children, page },
      });

      const data = res.data || {};
      const results = Array.isArray(data.results) ? data.results : [];

      // если сервер начал отдавать пусто — дальше смысла нет
      if (results.length === 0) break;

      // защита: если сервер снова отдаёт те же элементы (page "не работает")
      let newOnThisPage = 0;
      for (const item of results) {
        const id = item?.id;
        if (id == null) continue;
        if (!seenIds.has(id)) {
          seenIds.add(id);
          fullResults.push(item);
          newOnThisPage += 1;
        }
      }

      // если ничего нового не добавилось — значит пошёл повтор страниц
      if (newOnThisPage === 0) break;

      if (!version) version = data.version ?? null;
      if (totalCount === null && typeof data.count === 'number') totalCount = data.count;
      if (pageSize === null) pageSize = results.length || null;

      const nextVal = data.next;
      const hasNext =
        nextVal !== null && nextVal !== undefined && nextVal !== '' && nextVal !== 'null';

      // 1) Нормальный режим: есть next — идём пока next не исчезнет
      if (hasNext) {
        page += 1;
        continue;
      }

      // 2) Если next нет, но есть count — докачиваем по count
      if (typeof totalCount === 'number') {
        if (fullResults.length >= totalCount) break;

        // защита от бесконечного цикла
        if (pageSize === 0 || pageSize === null) break;

        page += 1;
        continue;
      }

      // 3) Нет next и нет count — дальше идти некуда
      break;
    }

    localStorage.setItem(cacheKey, JSON.stringify({ version, results: fullResults }));
    localStorage.setItem(lastCheckedKey, new Date().toISOString());

    return fullResults;
  } catch (e) {
    console.warn('❌ Ошибка загрузки select values:', e);
    return [];
  }
}
